import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import PivotTimer from "../components/tools/PivotTimer";
import MorningMission from "../components/tools/MorningMission";
import Reframer from "../components/tools/Reframer";
import ToolkitCard from "../components/ToolkitCard";
import { AlertTriangle, Package, ArrowRight, Music, Check } from "lucide-react";

// Distinct card for The Soundtrack (playlist product) — separate from workbook toolkits
const SoundtrackCard = ({ unlocked, onPurchase }) => (
  <div className="card-surface p-6 md:p-8 relative overflow-hidden" data-testid="soundtrack-card">
    <div className="absolute -right-16 -bottom-16 w-64 h-64 rounded-full bg-teal-500/10 blur-3xl pointer-events-none" />
    <div className="relative flex flex-col md:flex-row md:items-center gap-6 justify-between">
      <div className="flex items-start gap-4">
        <div className="p-3 rounded-xl bg-teal-500/10 border border-teal-500/30">
          <Music className="text-teal-300" size={22} />
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-teal-300/80 mb-1">Playlist · 100 tracks</div>
          <h3 className="font-display text-2xl mb-1">The Soundtrack</h3>
          <p className="text-sm text-neutral-400 max-w-lg">
            100 songs that got me through — with a note on why each one mattered. Angry, sad, happy, honest.
            You'll need your own YouTube Music (or free YT with ads).
          </p>
          <a href="/soundtrack/preview" data-testid="soundtrack-preview-link" className="text-xs text-teal-300 hover:text-teal-200 mt-2 inline-block">
            Hear a sample →
          </a>
        </div>
      </div>
      <div className="flex items-center gap-4 flex-shrink-0">
        <div className="text-right">
          <div className="font-display text-3xl text-teal-200">$14.99</div>
          <div className="text-xs text-neutral-500">one-time</div>
        </div>
        {unlocked ? (
          <a href="/soundtrack" data-testid="soundtrack-open-btn" className="btn-primary px-5 py-2.5 text-sm inline-flex items-center gap-2">
            <Check size={14} /> Open
          </a>
        ) : (
          <button
            onClick={onPurchase}
            data-testid="soundtrack-purchase-btn"
            className="btn-primary px-5 py-2.5 text-sm inline-flex items-center gap-2"
          >
            Unlock <ArrowRight size={14} />
          </button>
        )}
      </div>
    </div>
  </div>
);

const Studio = () => {
  const { user } = useAuth();
  const nav = useNavigate();
  const [library, setLibrary] = useState(null);

  useEffect(() => {
    if (!user) {
      setLibrary(null);
      return;
    }
    api.get("/me/library").then(({ data }) => setLibrary(data)).catch(() => {});
  }, [user]);

  const startCheckout = async (toolkitId) => {
    if (!user) {
      toast.info("Sign up to purchase — it's how we save your toolkit.");
      nav("/signup");
      return;
    }
    try {
      const { data } = await api.post("/payments/checkout", {
        toolkit_id: toolkitId,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
    }
  };

  const onClaimFree = async (email) => {
    try {
      await api.post("/toolkit/free-claim", { email });
      toast.success("Sent — check your inbox for The First 72 Hours.");
      if (user) {
        const { data } = await api.get("/me/library");
        setLibrary(data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send toolkit");
    }
  };

  const isUnlocked = (id) => library?.find((t) => t.id === id)?.unlocked;
  const allPaidUnlocked =
    isUnlocked("mapping_the_cycle") &&
    isUnlocked("inner_weather") &&
    isUnlocked("standing_in_the_storm");
  const allUnlocked = allPaidUnlocked && isUnlocked("the_soundtrack");

  return (
    <div className="fade-in max-w-6xl mx-auto px-6 py-16">
      <div className="mb-12 max-w-3xl">
        <div className="text-xs uppercase tracking-[0.24em] text-teal-300/80 mb-6">The Studio</div>
        <h1 className="font-display text-5xl md:text-6xl leading-tight">
          <span className="gradient-text">Tools you can</span>{" "}
          <span className="text-neutral-100">actually use today.</span>
        </h1>
        <p className="mt-6 text-neutral-400 text-base leading-relaxed">
          Try everything as a guest. Sign up to save your reframes, missions, and streak.
        </p>
      </div>

      <div className="mb-10 flex items-start gap-3 rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4">
        <AlertTriangle size={18} className="text-amber-300 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-amber-100/90">
          These tools are for reflection and support — not medical advice or diagnosis. If you're in crisis,
          please reach a professional or a hotline in your region.
        </p>
      </div>

      {/* Free tools */}
      <div className="grid md:grid-cols-2 gap-6 mb-16">
        <PivotTimer />
        <MorningMission />
        <div className="md:col-span-2">
          <Reframer />
        </div>
      </div>

      {/* Toolkit intro */}
      <div className="mb-8">
        <h2 className="font-display text-3xl md:text-4xl mb-3">
          <span className="text-neutral-100">The </span>
          <span className="gradient-text">Toolkits</span>
        </h2>
        <p className="text-neutral-400 max-w-2xl">
          Guided workbooks for the parts of recovery that don't fit a slogan. Read at your own pace. Nobody's watching.
        </p>
      </div>

      {/* Bundle offer */}
      {!allPaidUnlocked && (
        <div className="mb-8 card-surface p-6 md:p-8 relative overflow-hidden" data-testid="bundle-card">
          <div className="absolute -right-10 -top-10 w-48 h-48 rounded-full bg-teal-500/10 blur-3xl pointer-events-none" />
          <div className="relative flex flex-col md:flex-row md:items-center gap-6 justify-between">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-xl bg-teal-500/10 border border-teal-500/30">
                <Package className="text-teal-300" size={22} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-teal-300/80 mb-1">Workbook Bundle</div>
                <h3 className="font-display text-2xl mb-1">The Full Recovery Bundle</h3>
                <p className="text-sm text-neutral-400 max-w-lg">
                  All three paid workbooks — <span className="text-neutral-200">Mapping the Cycle</span>,{" "}
                  <span className="text-neutral-200">The Inner Weather</span>,{" "}
                  <span className="text-neutral-200">Standing in the Storm</span>. Save $4.98.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="text-sm text-neutral-500 line-through">$44.97</div>
                <div className="font-display text-3xl text-teal-200">$39.99</div>
              </div>
              <button
                onClick={() => startCheckout("recovery_bundle")}
                data-testid="bundle-purchase-btn"
                className="btn-primary px-6 py-3 inline-flex items-center gap-2 text-sm"
              >
                Unlock all three <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Everything Bundle offer */}
      {!allUnlocked && (
        <div className="mb-8 card-surface p-6 md:p-8 relative overflow-hidden border-teal-500/40" data-testid="everything-bundle-card">
          <div className="absolute -right-20 -bottom-20 w-64 h-64 rounded-full bg-teal-400/15 blur-3xl pointer-events-none" />
          <div className="relative flex flex-col md:flex-row md:items-center gap-6 justify-between">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-xl bg-teal-500/20 border border-teal-500/50">
                <Package className="text-teal-200" size={22} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-teal-300 mb-1">Best Value · Everything</div>
                <h3 className="font-display text-2xl mb-1">The Everything Bundle</h3>
                <p className="text-sm text-neutral-400 max-w-lg">
                  All three workbooks <span className="text-neutral-500">+</span>{" "}
                  <span className="text-neutral-200">The Soundtrack</span>. The full package. Save $14.98.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="text-sm text-neutral-500 line-through">$59.96</div>
                <div className="font-display text-3xl text-teal-200">$44.99</div>
              </div>
              <button
                onClick={() => startCheckout("everything_bundle")}
                data-testid="everything-bundle-purchase-btn"
                className="btn-primary px-6 py-3 inline-flex items-center gap-2 text-sm"
              >
                Unlock everything <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Individual toolkits */}
      <div className="grid md:grid-cols-2 gap-6">
        <ToolkitCard
          id="first_72_hours"
          title="The First 72 Hours"
          price="Free"
          description="A grounding starter pack for the first three days. Delivered by email."
          badge="Free · Email-gated"
          onPurchase={onClaimFree}
          isFree
          unlocked={isUnlocked("first_72_hours")}
        />
        <ToolkitCard
          id="mapping_the_cycle"
          title="Mapping the Cycle"
          price="$14.99"
          description="For the loops you keep finding yourself inside — behavioral patterns, substances, compulsive cycles."
          badge="Patterns · 8 sections"
          onPurchase={() => startCheckout("mapping_the_cycle")}
          unlocked={isUnlocked("mapping_the_cycle")}
        />
        <ToolkitCard
          id="inner_weather"
          title="The Inner Weather"
          price="$14.99"
          description="A gentle self-exploration for anxiety, depression, mood — without ever pretending to diagnose you."
          badge="Mental Health · 8 sections"
          onPurchase={() => startCheckout("inner_weather")}
          unlocked={isUnlocked("inner_weather")}
        />
        <ToolkitCard
          id="standing_in_the_storm"
          title="Standing in the Storm"
          price="$14.99"
          description="A companion for grief, major loss, and life transitions where 'move on' is the least helpful thing."
          badge="Hardship · 8 sections"
          onPurchase={() => startCheckout("standing_in_the_storm")}
          unlocked={isUnlocked("standing_in_the_storm")}
        />
        <div className="md:col-span-2">
          <SoundtrackCard unlocked={isUnlocked("the_soundtrack")} onPurchase={() => startCheckout("the_soundtrack")} />
        </div>
      </div>
    </div>
  );
};

export default Studio;
